import { Select, TextInput } from "@hisani/ui";
import { BadgeCheck } from "lucide-react";
import { useTranslations } from "next-intl";
import React from "react";
import {
  BreedFilterSchema,
  SexFilterSchema,
  type Filters,
} from "./marketplace-contracts";

export function MarketplaceFilterFields({
  filters,
  onChange,
  errorMessage,
}: {
  readonly filters: Filters;
  readonly onChange: (filters: Filters) => void;
  readonly errorMessage?: string;
}) {
  const t = useTranslations("Marketplace");
  return (
    <div className="grid gap-4">
      <SelectField
        label={t("breed")}
        value={filters.breed}
        onChange={(breed) =>
          onChange({ ...filters, breed: BreedFilterSchema.parse(breed) })
        }
        options={[
          ["", t("allBreeds")],
          ["PUREBRED_ARABIAN", t("breedArabian")],
          ["THOROUGHBRED", t("breedThoroughbred")],
          ["OTHER", t("breedOther")],
        ]}
      />
      <SelectField
        label={t("sex")}
        value={filters.sex}
        onChange={(sex) =>
          onChange({ ...filters, sex: SexFilterSchema.parse(sex) })
        }
        options={[
          ["", t("allSexes")],
          ["COLT", t("sexColt")],
          ["FILLY", t("sexFilly")],
          ["GELDING", t("sexGelding")],
          ["MARE", t("sexMare")],
          ["STALLION", t("sexStallion")],
        ]}
      />
      <div className="grid grid-cols-2 gap-3">
        <NumberField
          label={t("minimumAge")}
          placeholder={t("noMinimum")}
          value={filters.minimumAge}
          min="0"
          max="40"
          onChange={(minimumAge) => onChange({ ...filters, minimumAge })}
        />
        <NumberField
          label={t("maximumAge")}
          placeholder={t("noMaximum")}
          value={filters.maximumAge}
          min="0"
          max="40"
          onChange={(maximumAge) => onChange({ ...filters, maximumAge })}
        />
      </div>
      <SelectField
        label={t("filterCurrency")}
        value={filters.currency}
        onChange={() => undefined}
        options={[["AED", t("currencyAed")]]}
      />
      <div className="grid grid-cols-2 gap-3">
        <NumberField
          label={t("minimumPrice")}
          placeholder={t("noMinimum")}
          value={filters.minimumPrice}
          min="0"
          max="9999999999999.99"
          step="0.01"
          onChange={(minimumPrice) => onChange({ ...filters, minimumPrice })}
        />
        <NumberField
          label={t("maximumPrice")}
          placeholder={t("noMaximum")}
          value={filters.maximumPrice}
          min="0"
          max="9999999999999.99"
          step="0.01"
          onChange={(maximumPrice) => onChange({ ...filters, maximumPrice })}
        />
      </div>
      <label className="text-sm font-bold">
        {t("location")}
        <TextInput
          type="text"
          value={filters.location}
          onChange={(event) =>
            onChange({ ...filters, location: event.target.value })
          }
          placeholder={t("locationPlaceholder")}
          className="mt-2 min-h-12"
        />
      </label>
      <label className="flex min-h-12 cursor-pointer items-center gap-3 text-sm font-bold">
        <input
          type="checkbox"
          checked={filters.verifiedOnly}
          onChange={(event) =>
            onChange({ ...filters, verifiedOnly: event.target.checked })
          }
          className="size-5 rounded-xs accent-[var(--color-action-accent-default)]"
        />
        <span className="inline-flex items-center gap-2">
          <BadgeCheck className="size-4 text-brand-ink" aria-hidden="true" />
          {t("verifiedOnly")}
        </span>
      </label>
      {errorMessage === undefined ? null : (
        <p role="alert" className="text-sm font-semibold text-danger">
          {errorMessage}
        </p>
      )}
    </div>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  step = "1",
  placeholder,
  onChange,
}: {
  readonly label: string;
  readonly value: string;
  readonly min: string;
  readonly max: string;
  readonly step?: string;
  readonly placeholder: string;
  readonly onChange: (value: string) => void;
}) {
  return (
    <label className="text-sm font-bold">
      {label}
      <TextInput
        type="number"
        inputMode={step === "1" ? "numeric" : "decimal"}
        min={min}
        max={max}
        step={step}
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2 min-h-12"
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly options: readonly (readonly [string, string])[];
}) {
  return (
    <label className="text-sm font-bold">
      {label}
      <Select className="mt-2" value={value} onValueChange={onChange}>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>
            {optionLabel}
          </option>
        ))}
      </Select>
    </label>
  );
}
